import { useEffect, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiDollarSign,
  FiFilter,
  FiTrendingDown,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";
import { MdAccountBalanceWallet } from "react-icons/md";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioFinanceiro = () => {
  const [dados, setDados] = useState({
    contas_pagar: [],
    contas_receber: [],
    fluxo_caixa: {
      total_entradas: 0,
      total_saidas: 0,
      saldo: 0,
      movimentacoes: []
    }
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("resumo");

  const [categorias, setCategorias] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    categorias: useRef(null),
    centrosCusto: useRef(null),
    status: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    categorias: [],
    centrosCusto: [],
    status: [],
    data_inicio: "",
    data_fim: "",
    tipo: "todos"
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [categoriasRes, centrosRes] = await Promise.all([
        listarTudo("/financeiro/categorias-financeiras/"),
        listarTudo("/financeiro/centro-custo/"),
      ]);

      setCategorias(Array.isArray(categoriasRes.data) ? categoriasRes.data : categoriasRes.data.results || []);
      setCentrosCusto(Array.isArray(centrosRes.data) ? centrosRes.data : centrosRes.data.results || []);
      // Não há endpoint de status no backend — usa a lista estática de status.
      // Ids batem com os valores que RelatorioFinanceiro normaliza (ver
      // api/relatorios/views.py) — "pago" vira "paga"/"recebida" conforme o
      // tipo de conta, os demais têm a mesma grafia nos dois models.
      setStatusList([
        { id: "pago", nome: "Pago / Recebido" },
        { id: "pendente", nome: "Pendente" },
        { id: "atrasado", nome: "Atrasado" },
        { id: "cancelado", nome: "Cancelado" },
        { id: "parcial", nome: "Parcial" },
      ]);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setCategorias([]);
      setCentrosCusto([]);
      setStatusList([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.categorias && filtros.categorias.length > 0) {
        params.categorias = filtros.categorias.join(",");
      }
      if (filtros.centrosCusto && filtros.centrosCusto.length > 0) {
        params.centros_custo = filtros.centrosCusto.join(",");
      }
      if (filtros.status && filtros.status.length > 0) {
        params.status = filtros.status.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }
      if (filtros.tipo && filtros.tipo !== "todos") {
        params.tipo = filtros.tipo;
      }

      console.log("🌐 URL:", `/relatorios/financeiro/contas/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/financeiro/contas/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      categorias: [],
      centrosCusto: [],
      status: [],
      data_inicio: "",
      data_fim: "",
      tipo: "todos"
    });
  };

  const exportarExcel = () => {
    if (!dados) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Planilha de Contas a Pagar
    if (dados.contas_pagar && dados.contas_pagar.length > 0) {
      const wsPagar = XLSX.utils.json_to_sheet(dados.contas_pagar);
      XLSX.utils.book_append_sheet(workbook, wsPagar, "Contas a Pagar");
    }

    // Planilha de Contas a Receber
    if (dados.contas_receber && dados.contas_receber.length > 0) {
      const wsReceber = XLSX.utils.json_to_sheet(dados.contas_receber);
      XLSX.utils.book_append_sheet(workbook, wsReceber, "Contas a Receber");
    }

    // Planilha de Fluxo de Caixa
    if (dados.fluxo_caixa && dados.fluxo_caixa.movimentacoes && dados.fluxo_caixa.movimentacoes.length > 0) {
      const wsFluxo = XLSX.utils.json_to_sheet(dados.fluxo_caixa.movimentacoes);
      XLSX.utils.book_append_sheet(workbook, wsFluxo, "Fluxo de Caixa");
    }

    XLSX.writeFile(workbook, "relatorio_financeiro.xlsx");
  };

  // Combina contas a pagar e a receber num único conjunto de linhas — o
  // utilitário compartilhado exporta uma tabela por vez, então diferenciamos
  // com a coluna TIPO em vez das duas páginas separadas do PDF manual antigo.
  const exportarPDF = async () => {
    const linhas = [
      ...(dados.contas_pagar || []).map((item) => ({ ...item, tipo: "Pagar" })),
      ...(dados.contas_receber || []).map((item) => ({ ...item, tipo: "Receber" })),
    ];

    await exportarRelatorioPDF({
      titulo: "Relatório Financeiro - Contas a Pagar e Receber",
      colunas: [
        { key: "tipo", label: "TIPO" },
        { key: "data", label: "DATA" },
        { key: "descricao", label: "DESCRIÇÃO" },
        { key: "valor", label: "VALOR" },
        { key: "categoria", label: "CATEGORIA" },
        { key: "centro_custo", label: "CENTRO CUSTO" },
        { key: "status", label: "STATUS" },
      ],
      dados: linhas,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor, coluna) => (coluna.key === "valor" ? formatarMoeda(valor) : (valor ?? "-")),
    });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  // Rótulos vêm de conta.get_status_display() no backend: "Paga"/"Recebida"
  // (quitada), "Atrasada", "Cancelada", "Parcial" ou "Pendente".
  const classeBadgeStatus = (status) => {
    switch (status) {
      case 'Paga':
      case 'Recebida':
        return 'bg-green-100 text-green-800';
      case 'Atrasada':
        return 'bg-red-100 text-red-800';
      case 'Cancelada':
        return 'bg-gray-100 text-gray-700';
      case 'Parcial':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const classePrintStatus = (status) => {
    switch (status) {
      case 'Paga':
      case 'Recebida':
        return 'pago';
      case 'Atrasada':
        return 'atrasada';
      case 'Cancelada':
        return 'cancelada';
      case 'Parcial':
        return 'parcial';
      default:
        return 'pendente';
    }
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio Financeiro</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            h2 { color: #555; font-size: 14px; margin-top: 20px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .resumo { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
            .resumo-card { background: #f0f0f0; padding: 15px; border-radius: 8px; text-align: center; min-width: 150px; }
            .resumo-card .valor { font-size: 24px; font-weight: bold; }
            .resumo-card .label { font-size: 12px; color: #666; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 20px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .pago { color: green; font-weight: bold; }
            .pendente { color: #b45309; font-weight: bold; }
            .atrasada { color: red; font-weight: bold; }
            .cancelada { color: #6b7280; font-weight: bold; }
            .parcial { color: #1d4ed8; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
              .resumo-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio Financeiro - Contas a Pagar e Receber</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          </div>

          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Periodo:</strong> ${filtros.data_inicio || 'inicio'} a ${filtros.data_fim || 'atual'}
          </div>
          ` : ''}

          <div class="resumo">
            <div class="resumo-card">
              <div class="valor" style="color: #dc3545;">${formatarMoeda(dados.fluxo_caixa?.total_saidas || 0)}</div>
              <div class="label">Total a Pagar</div>
            </div>
            <div class="resumo-card">
              <div class="valor" style="color: #28a745;">${formatarMoeda(dados.fluxo_caixa?.total_entradas || 0)}</div>
              <div class="label">Total a Receber</div>
            </div>
            <div class="resumo-card">
              <div class="valor" style="color: ${(dados.fluxo_caixa?.total_entradas || 0) - (dados.fluxo_caixa?.total_saidas || 0) >= 0 ? '#28a745' : '#dc3545'}">
                ${formatarMoeda((dados.fluxo_caixa?.total_entradas || 0) - (dados.fluxo_caixa?.total_saidas || 0))}
              </div>
              <div class="label">Saldo</div>
            </div>
          </div>

          <h2>📋 Contas a Pagar</h2>
          <table>
            <thead>
              <tr><th>DATA</th><th>DESCRICAO</th><th>VALOR</th><th>CATEGORIA</th><th>CENTRO CUSTO</th><th>STATUS</th></tr>
            </thead>
            <tbody>
              ${dados.contas_pagar?.map(item => `
                <tr>
                  <td>${item.data || '-'}</td>
                  <td>${item.descricao || '-'}</td>
                  <td>${formatarMoeda(item.valor)}</td>
                  <td>${item.categoria || '-'}</td>
                  <td>${item.centro_custo || '-'}</td>
                  <td class="${classePrintStatus(item.status)}">${item.status || '-'}</td>
                </tr>
              `).join('') || '<tr><td colspan="6">Nenhuma conta a pagar encontrada</td></tr>'}
            </tbody>
          </table>

          <h2>💰 Contas a Receber</h2>
          <table>
            <thead>
              <tr><th>DATA</th><th>DESCRICAO</th><th>VALOR</th><th>CATEGORIA</th><th>CENTRO CUSTO</th><th>STATUS</th></tr>
            </thead>
            <tbody>
              ${dados.contas_receber?.map(item => `
                <tr>
                  <td>${item.data || '-'}</td>
                  <td>${item.descricao || '-'}</td>
                  <td>${formatarMoeda(item.valor)}</td>
                  <td>${item.categoria || '-'}</td>
                  <td>${item.centro_custo || '-'}</td>
                  <td class="${classePrintStatus(item.status)}">${item.status || '-'}</td>
                </tr>
              `).join('') || '<tr><td colspan="6">Nenhuma conta a receber encontrada</td></tr>'}
            </tbody>
          </table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-brand-green rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={MdAccountBalanceWallet}
            titulo="Relatório Financeiro"
            descricao="Consulte contas a pagar, contas a receber e fluxo de caixa integrados"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipo de Conta */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Conta
                </label>
                <select
                  name="tipo"
                  value={filtros.tipo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="todos">Todos</option>
                  <option value="pagar">Contas a Pagar</option>
                  <option value="receber">Contas a Receber</option>
                </select>
              </div>

              {/* Categorias - Multi Select */}
              <MultiSelect
                label="Categorias"
                options={categorias}
                selectedValues={filtros.categorias}
                fieldName="categorias"
                refProp={dropdownRefs.categorias}
              />

              {/* Centros de Custo - Multi Select */}
              <MultiSelect
                label="Centros de Custo"
                options={centrosCusto}
                selectedValues={filtros.centrosCusto}
                fieldName="centrosCusto"
                refProp={dropdownRefs.centrosCusto}
              />

              {/* Status - Multi Select */}
              <MultiSelect
                label="Status"
                options={statusList}
                selectedValues={filtros.status}
                fieldName="status"
                refProp={dropdownRefs.status}
              />

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.categorias && filtros.categorias.length > 0) ||
              (filtros.centrosCusto && filtros.centrosCusto.length > 0) ||
              (filtros.status && filtros.status.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.categorias && filtros.categorias.map((id) => {
                    const categoria = categorias.find(c => c.id === id);
                    return categoria ? (
                      <span key={`categoria-${id}`} className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Categoria: {categoria.nome}
                        <button onClick={() => handleMultiSelect("categorias", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {filtros.centrosCusto && filtros.centrosCusto.map((id) => {
                    const centro = centrosCusto.find(c => c.id === id);
                    return centro ? (
                      <span key={`centro-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Centro: {centro.nome}
                        <button onClick={() => handleMultiSelect("centrosCusto", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {filtros.status && filtros.status.map((id) => {
                    const stat = statusList.find(s => s.id === id);
                    return stat ? (
                      <span key={`status-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Status: {stat.nome}
                        <button onClick={() => handleMultiSelect("status", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MdAccountBalanceWallet size={22} className="text-brand-green" />
                Relatório Financeiro
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Abas */}
            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setAbaAtiva("resumo")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "resumo" ? "border-b-2 border-brand-green text-brand-green" : "text-gray-500 hover:text-gray-700"}`}
                >
                  📊 Resumo
                </button>
                <button
                  onClick={() => setAbaAtiva("pagar")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "pagar" ? "border-b-2 border-red-500 text-red-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🔴 Contas a Pagar
                </button>
                <button
                  onClick={() => setAbaAtiva("receber")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "receber" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🟢 Contas a Receber
                </button>
                <button
                  onClick={() => setAbaAtiva("fluxo")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "fluxo" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  💰 Fluxo de Caixa
                </button>
              </div>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Conteudo */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {/* Aba Resumo */}
              {abaAtiva === "resumo" && (
                <div>
                  {/* Cards de Resumo */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <CartaoEstatistica
                      icone={FiTrendingDown}
                      rotulo="Total a Pagar"
                      valor={formatarMoeda(dados.fluxo_caixa?.total_saidas || 0)}
                      subvalor={`${dados.contas_pagar?.length || 0} transações`}
                      cor="vermelho"
                    />
                    <CartaoEstatistica
                      icone={FiTrendingUp}
                      rotulo="Total a Receber"
                      valor={formatarMoeda(dados.fluxo_caixa?.total_entradas || 0)}
                      subvalor={`${dados.contas_receber?.length || 0} transações`}
                      cor="verde"
                    />
                    <CartaoEstatistica
                      icone={FiDollarSign}
                      rotulo="Saldo"
                      valor={formatarMoeda((dados.fluxo_caixa?.total_entradas || 0) - (dados.fluxo_caixa?.total_saidas || 0))}
                      cor={(dados.fluxo_caixa?.total_entradas || 0) - (dados.fluxo_caixa?.total_saidas || 0) >= 0 ? "azul" : "dourado"}
                    />
                  </div>

                  {/* Grafico Simples - Ultimas movimentacoes */}
                  <Cartao titulo="Últimas Movimentações">
                    {(dados.fluxo_caixa?.movimentacoes || []).length > 0 ? (
                      <div className="space-y-3">
                        {(dados.fluxo_caixa?.movimentacoes || []).slice(0, 10).map((mov, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${mov.tipo === 'entrada' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <div>
                                <div className="font-medium text-gray-800">{mov.descricao}</div>
                                <div className="text-xs text-gray-500">{mov.data} • {mov.categoria} • {mov.centro_custo}</div>
                              </div>
                            </div>
                            <div className={`font-bold ${mov.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                              {mov.tipo === 'entrada' ? '+' : '-'} {formatarMoeda(mov.valor)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EstadoVazio descricao="Ainda não há movimentações para o período selecionado." />
                    )}
                  </Cartao>
                </div>
              )}

              {/* Aba Contas a Pagar */}
              {abaAtiva === "pagar" && (
                <div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-red-800 font-medium">Total de Contas a Pagar</span>
                      <span className="text-2xl font-bold text-red-700">{formatarMoeda(dados.fluxo_caixa?.total_saidas || 0)}</span>
                    </div>
                  </div>
                  {dados.contas_pagar && dados.contas_pagar.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DATA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DESCRICAO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VALOR</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CATEGORIA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CENTRO CUSTO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">STATUS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.contas_pagar?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600">{item.data || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.descricao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-red-600 font-medium">{formatarMoeda(item.valor)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.categoria || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.centro_custo || "-"}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${classeBadgeStatus(item.status)}`}>
                                  {item.status || "-"}
                                </span>
                               </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio titulo="Nenhuma conta a pagar encontrada" descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* Aba Contas a Receber */}
              {abaAtiva === "receber" && (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-green-800 font-medium">Total de Contas a Receber</span>
                      <span className="text-2xl font-bold text-green-700">{formatarMoeda(dados.fluxo_caixa?.total_entradas || 0)}</span>
                    </div>
                  </div>
                  {dados.contas_receber && dados.contas_receber.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DATA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DESCRICAO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VALOR</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CATEGORIA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CENTRO CUSTO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">STATUS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.contas_receber?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600">{item.data || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.descricao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-green-600 font-medium">{formatarMoeda(item.valor)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.categoria || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.centro_custo || "-"}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${classeBadgeStatus(item.status)}`}>
                                  {item.status || "-"}
                                </span>
                               </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio titulo="Nenhuma conta a receber encontrada" descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* Aba Fluxo de Caixa */}
              {abaAtiva === "fluxo" && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="text-green-700 font-medium">Total de Entradas</div>
                      <div className="text-2xl font-bold text-green-700">{formatarMoeda(dados.fluxo_caixa?.total_entradas || 0)}</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="text-red-700 font-medium">Total de Saídas</div>
                      <div className="text-2xl font-bold text-red-700">{formatarMoeda(dados.fluxo_caixa?.total_saidas || 0)}</div>
                    </div>
                  </div>
                  {(dados.fluxo_caixa?.movimentacoes || []).length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DATA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">DESCRICAO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">TIPO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VALOR</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CATEGORIA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CENTRO CUSTO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados.fluxo_caixa?.movimentacoes || []).map((mov, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600">{mov.data || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{mov.descricao || "-"}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${mov.tipo === 'entrada' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {mov.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-sm font-semibold ${mov.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                                {mov.tipo === 'entrada' ? '+' : '-'} {formatarMoeda(mov.valor)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{mov.categoria || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{mov.centro_custo || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Ainda não há movimentações para o período selecionado." />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioFinanceiro;
